import React from "react";

import { ReactComponent as PinIconSvg } from "../assets/svg/pin.svg";
import { ReactComponent as EmailIconSvg } from "../assets/svg/email.svg";
import { ReactComponent as UserIconSvg } from "../assets/svg/user.svg";

import { useAppContextMethods, useGlobalUser } from "../state";

import * as Yup from "yup";
import { useForm } from "react-yup";

import cx from "classnames";

import { IconField, Checkbox, Radio, ValidationError } from "~/ui";

import { STATE_LIST } from "~/constants";
import { subscribeNewsletter, upsertHealthyWorkplace } from "~/util";

const SCHEMA = Yup.object({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  email: Yup.string().email().required("Email is required"),
  confirmEmail: Yup.string()
    .oneOf([Yup.ref("email")], "Please enter the same email")
    .required("Please enter the same email"),
  subscribeNewsletter: Yup.boolean().default(false),
  state: Yup.string()
    .required("Select a state. If you do not live in Australia, select 'Other'")
    .nullable()
    .oneOf(
      STATE_LIST.map((n) => n[0]),
      "Select a state. If you do not live in Australia, select 'Other'",
    ),
  isOver18: Yup.boolean().required().oneOf([true]),
}).defined();

export const Registration: React.FC<{}> = () => {
  const [user, setUser] = useGlobalUser();

  const { scrollToTop } = useAppContextMethods();

  const {
    createSubmitHandler,
    field,
    values,
    touched,
    errors,
    isSubmitting,
  } = useForm({
    validationSchema: SCHEMA,
    defaultValues: () => {
      console.log(user);
      if (user) {
        return user;
      }

      return {};
    },
  });

  const handleSubmit = React.useMemo(() => {
    return createSubmitHandler((v: any) => {
      const promises: Promise<any>[] = [];

      if (v.subscribeNewsletter) {
        promises.push(
          subscribeNewsletter({
            email: v.email!,
            firstName: v.firstName!,
            isOverEighteen: v.isOver18!,
            lastName: v.lastName!,
            state: v.state!,
          }),
        );
      }

      promises.push(
        // todo : api endpoint
        upsertHealthyWorkplace({
          email: v.email!,
          firstName: v.firstName!,
          lastName: v.lastName!,
          state: v.state!,
        }),
      );

      Promise.allSettled(promises).then((p) => {
        scrollToTop();
        setUser((u) => ({
          ...u,
          firstName: v.firstName!,
          lastName: v!.lastName!,
          email: v!.email!,
          state: v.state!,
          currentModule: 1,
          view: "module",
          startedModules: {
            ...u.startedModules,
            1: true,
          },
        }));
      });
    });
  }, [createSubmitHandler]);

  return (
    <section className="pt-5">
      <div className="page-header block-blueberry mb-4">
        <h1>Registration</h1>
      </div>
      <div className="row">
        <div className="col-md-8 mx-auto">
          <p className="text-center mb-3">
            To undertake the training module please register your details below.
            If you started your training on another device, you can continue
            your progress where you left off by going back to that device.
          </p>

          <div className="card shadow-sm bg-light">
            <div className="p-4">
              <h2 className="text-warning text-center mb-4">
                Register for workplace health training
              </h2>
              <form action="" onSubmit={handleSubmit} method="post" noValidate>
                <div className="row">
                  <div className="col-sm-6">
                    <IconField
                      className="mb-4"
                      icon={UserIconSvg}
                      label="First Name"
                      name="firstName"
                      placeholder="First name"
                      error={errors.firstName || ""}
                      touched={!!touched.firstName}
                      value={values.firstName || ""}
                      {...field}
                    />
                  </div>
                  <div className="col-sm-6">
                    <IconField
                      className="mb-4"
                      icon={UserIconSvg}
                      label="Last Name"
                      name="lastName"
                      placeholder="Last name"
                      error={errors.lastName || ""}
                      touched={!!touched.lastName}
                      value={values.lastName || ""}
                      {...field}
                    />
                  </div>
                  <div className="col-sm-6">
                    <IconField
                      className="mb-4"
                      icon={EmailIconSvg}
                      label="Email"
                      name="email"
                      placeholder="Email address"
                      error={errors.email || ""}
                      touched={!!touched.email}
                      value={values.email || ""}
                      {...field}
                    />
                  </div>
                  <div className="col-sm-6">
                    <IconField
                      className="mb-4"
                      icon={EmailIconSvg}
                      label="Confirm email"
                      name="confirmEmail"
                      placeholder="Email, again"
                      error={errors.confirmEmail || ""}
                      touched={!!touched.confirmEmail}
                      value={values.confirmEmail || ""}
                      {...field}
                    />
                  </div>
                  <div className="col-sm-6">
                    <div className="form-group mb-4">
                      <label htmlFor="state">State</label>
                      <div className="input-group mb-3">
                        <div className="input-group-prepend">
                          <span className="input-group-text" id="state-addon">
                            <PinIconSvg width={18} height={18} />
                          </span>
                        </div>
                        <select
                          id="state"
                          name="state"
                          className={cx("custom-select", {
                            "is-invalid": errors.state && touched.state,
                          })}
                          value={values.state || 0}
                          {...field}
                        >
                          <option disabled value={0}>
                            Select your state
                          </option>
                          {STATE_LIST.map((state, idx) => (
                            <option key={idx} value={state[0]}>
                              {state[1]}
                            </option>
                          ))}
                        </select>
                        {errors.state && touched.state && (
                          <ValidationError>{errors.state}</ValidationError>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <Checkbox
                    label="YES! I want to receive the LiveLighter e-newsletter
                          for free tips, advice and more!"
                    name="subscribeNewsletter"
                    touched={!!touched.subscribeNewsletter}
                    error={errors.subscribeNewsletter || ""}
                    checked={values.subscribeNewsletter === true}
                    {...field}
                  />
                </div>

                <div className="form-group">
                  <Checkbox
                    label="Tick here to confirm you are over 18."
                    name="isOver18"
                    touched={!!touched.isOver18}
                    error={errors.isOver18 || ""}
                    checked={values.isOver18 === true}
                    {...field}
                  />
                  <p>
                    LiveLighter is a campaign designed and funded for adults.
                  </p>
                </div>

                <p className="small">
                  By creating an account, I accept LiveLighter's{" "}
                  <a
                    href="https://livelighter.com.au/terms-and-conditions"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    terms and conditions
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://livelighter.com.au/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    privacy note and collection statement
                  </a>
                  .
                </p>

                <footer className="text-center">
                  <button
                    className="btn btn-lg btn-secondary"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Please wait..." : "Start Module 1"}
                  </button>
                </footer>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
